import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	MaterialReactTable,
	useMaterialReactTable,
	MRT_ShowHideColumnsButton,
} from "material-react-table";

import "./Corrections.scss";

import { Button, COLORS, Icon, Modal } from "abs-ui-components";

import CorrectionsChildTable from "./CorrectionsChildTable";
import AbsModal from "../../../common/absModal/AbsModal";
import CorrectionRequestForm from "./CorrectionRequestForm";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { getCorrectionsData } from "../../../services/ChildReportService";
import { convertLocaltoUTCFormat } from "../../../services/DateFormatUtility";
import { GetReportFlagsByStatus } from "../services";
import { reportsActions } from "../../../store/reports/reports.slice";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Corrections = ({ reportDetails }) => {
	const [correctionData, setCorretionData] = useState([]);
	const [correctionDataEnabled, setCorretionDataEnabled] = useState(false);
	const [correctionRequestAccess, setCorrectionRequestAccess] =
		useState(false);
	const [isCorrectionReqModalOpen, setIsCorrectionReqModalOpen] =
		useState(false);
	const [correctionConfirmDelete, setCorrectionConfirmDelete] =
		useState(false);
	const [rowSelection, setRowSelection] = useState({});
	const [selectAll, setSelectAll] = useState(false);
	const [enableResolveBtn, setEnableResolveBtn] = useState(true);
	const dispatch = useDispatch();

	const reportFlags = useSelector((state) => state.reports.reportFlagDetails);

	const toggleCorrectionRequest = () => {
		setIsCorrectionReqModalOpen(!isCorrectionReqModalOpen);
	};

	const fetchData = async () => {
		try {
			const corrections = await getCorrectionsData(
				reportDetails?.ReportMstId
			);
			let transformedData = corrections?.data?.corrections.map(
				(item) => ({
					...item,
					ApplicableReports: item.reportCorrectionsAppliedDtls
						?.map((item) => item.reportId)
						.join(", "),
					CorrectionType: item.reportCorrectionsTypeDtls
						?.map((item) => item.correctionType)
						.join(", "),
				})
			);
			transformedData = transformedData?.sort((a, b) => {
				return new Date(a.createdDate) - new Date(b.createdDate);
			});
			setCorretionData(transformedData || []);
			setCorretionDataEnabled(corrections?.Enabled);
			setCorrectionRequestAccess(reportFlags?.IsCorrectionRequestAllowed);
		} catch (error) {
			console.error("Failed to fetch corrections:", error);
		}
	};
	useEffect(() => {
		if (reportDetails?.ReportMstId) {
			fetchData();
		}
	}, [reportDetails?.ReportMstId]);

	const updateReportFlags = () => {
		GetReportFlagsByStatus(reportDetails?.ReportMstId).then((response) => {
			dispatch(
				reportsActions.setReportFlagDetails({
					reportFlagDetails: response,
				})
			);
		});
	};


	const handleResolveCorrectionRequest = (selectedCorrectionRequests) => {
		if (selectedCorrectionRequests?.length > 0) {
			axiosHttp({
				url: `${BASE_URL}api/Corrections/ResolvedCorrections`,
				method: "POST",
				data: {
					reportCorrectionsDtlIds: selectedCorrectionRequests,
				},
			})
				.then((res) => {
					if (res?.data?.isSuccess) {
						emitToasterMsg(
							"success",
							"Correction Request",
							"Request Resolved Successfully"
						);
						setCorrectionConfirmDelete(false);
						setRowSelection({});
						fetchData();
						updateReportFlags(); // Update report flags after resolving
					}
				})
				.catch((err) => {
					setCorrectionConfirmDelete(false);
					emitToasterMsg("error", "Correction Request", err.message);
				});
		} else {
			emitToasterMsg(
				"error",
				"Correction Request",
				"Please select atleast one Request"
			);
		}
	};

	// (delete method)
	const handleDeletingCorrectRequests = (selectedDeleteCorrectionRows) => {
		// setLoading(true);

		axiosHttp({
			url: `${BASE_URL}api/Corrections/DeleteCorrections`,
			method: "DELETE",
			data: { reportCorrectionsDtlIds: selectedDeleteCorrectionRows },
		})
			.then((res) => {
				if (res.data) {
					emitToasterMsg(
						"success",
						"Correction Request",
						"Deleted Successfully"
					);
					fetchData();
					setCorrectionConfirmDelete(false);
					setRowSelection({});
					updateReportFlags(); // Update report flags after deletion
				}
			})
			.catch((err) => {
				setCorrectionConfirmDelete(false);
				emitToasterMsg("error", "Correction Request", err.message);
			});
	};

	useEffect(() => {
		const result = correctionData?.length > 0 && correctionData?.some(item => Object.keys(rowSelection).includes(item?.reportCorrectionsDtlId + '') && item?.status !== "Resolved");
		setEnableResolveBtn(!result);
	}, [rowSelection])

	const correctionListColumns = useMemo(
		() => [
			{
				accessorKey: "correctionHeader",
				header: "Correction Request",
			},
			{
				accessorKey: "CorrectionType",
				header: "Correction Type",
			},
			{
				accessorKey: "ApplicableReports",
				header: "Applicable Reports",
			},
			{
				accessorKey: "status",
				header: "Status",
			},
			{
				accessorKey: "createdBy",
				header: "Created By",
			},
			{
				accessorKey: "createdDate",
				header: "Created",
				accessorFn: (row) => {
					if (row.createdDate) {
						return convertLocaltoUTCFormat(row.createdDate);
					}
				},
			},
		],
		[]
	);

	const handleSelectAll = (event, field) => {
		setSelectAll(event.target.checked);
	};

	const table = useMaterialReactTable({
		columns: correctionListColumns,
		data: correctionData,
		enableExpandAll: false,
		enableFilters: false,
		enableSorting: false,
		enableRowSelection: true,
		enableColumnPinning: false,
		enableTopToolbar: reportFlags?.IsCorrectionRequestAllowed
			? true
			: false,
		enableBottomToolbar: false,
		onRowSelectionChange: setRowSelection,
		state: { rowSelection },
		getRowId: (row) => row.reportCorrectionsDtlId,
		muiDetailPanelProps: () => ({
			sx: {
				padding: 0,
				margin: 0,
			},
		}),
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ShowHideColumnsButton
					id="vessel_view_show_hide_Columns"
					table={table}
				/>
			</>
		),
		renderTopToolbarCustomActions: () => {
			return (
				<>
					<div className="container-fluid p-1">
						<div className="row p-0 m-0 d-flex align-items-center mt-2">
							{reportFlags?.IsCorrectionRequestAllowed && (
								<>
									<Button
										startIcon={<Icon icon="add" />}
										variant="secondary"
										className="ml-2"
										onClick={toggleCorrectionRequest}
									>
										NEW CORRECTION REQUEST
									</Button>
									<Button
										startIcon={<Icon icon="check" />}
										onClick={() =>
											handleResolveCorrectionRequest(
												Object.keys(rowSelection).map(
													Number
												)
											)
										}
										variant="secondary"
										className="ml-4"
										disabled={enableResolveBtn}
									>
										RESOLVE CORRECTION REQUEST
									</Button>
									<Button
										onClick={() =>
											setCorrectionConfirmDelete(true)
										}
										primaryBackgroundColor={COLORS.RED.DARK}
										primaryColor={COLORS.RED.DARK}
										disabled={
											Object.keys(rowSelection)
												?.length === 0
												? true
												: false
										}
										startIcon={<Icon icon="delete" />}
										className="ml-4 delete-btn"
									>
										DELETE
									</Button>
								</>
							)}
						</div>
					</div>
				</>
			);
		},
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				lineHeight: "40px",
				color: "rgb(255 255 255)",
				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiExpandButtonProps: ({ row, table }) => ({
			onClick: () =>
				table.setExpanded({ [row.id]: !row.getIsExpanded() }),
			sx: {
				transform: row.getIsExpanded()
					? "rotate(180deg)"
					: "rotate(-90deg)",
				transition: "transform 0.2s",
			},
		}),
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			id: "test1",
			onClick: (e, field) => handleSelectAll(e, field),
			disabled: correctionData.length === 0,
			sx: {
				color: "white",
			},
		},

		renderDetailPanel: ({ row }) => {
			return (
				<div className="mx-5">
					<CorrectionsChildTable
						parentCorrectionData={row.original}
						reportDetails={reportDetails}
						fetchData={fetchData}
					/>
				</div>
			);
		},
	});

	return (
		<>
			<div className="custom-table-wrapper">
				<MaterialReactTable table={table} />
			</div>
			<div className="corrections-request-form">
				{isCorrectionReqModalOpen && (
					<AbsModal
						isOpen={isCorrectionReqModalOpen}
						title="Correction Request"
						onClose={setIsCorrectionReqModalOpen}
						close={toggleCorrectionRequest}
					>
						<CorrectionRequestForm
							toggleCorrection={toggleCorrectionRequest}
							reportDetails={reportDetails}
							fetchData={fetchData}
						/>
					</AbsModal>
				)}
			</div>

			<div className="confirmation-modal-container">
				<Modal
					isOpen={correctionConfirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setCorrectionConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4 footer">
							<Button
								className="delete-btn"
								onClick={() =>
									handleDeletingCorrectRequests(
										Object.keys(rowSelection).map(Number)
									)
								}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setCorrectionConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>
						Are you sure you want to delete the correction(s)?
					</div>
				</Modal>
			</div>
		</>
	);
};

export default Corrections;
